"use client"

import { useId } from "react"
import { area, curveMonotoneX, line } from "d3-shape"
import { scaleLinear } from "d3-scale"

import { HOURS_BIN_EDGES } from "@/modules/lernpfad/engine/aggregate"
import { SEQUENTIAL_VAR, STATUS_VAR } from "./palette"

/**
 * Planned hours across the whole population, standard against tuned.
 *
 * Form choice: the question is DISTRIBUTION, and no summary statistic answers
 * it. "Average hours rose by 3" is compatible with everybody doing slightly
 * more and with a tenth of the workforce doubling while the rest is untouched —
 * two situations an L&D team would act on very differently. Overlaying the two
 * curves shows which one it is at a glance.
 *
 * Two curves rather than paired bars: a change here is a change in SHAPE, and
 * shape reads better as an outline than as forty small rectangles. The tuned
 * curve is filled and the baseline is a dashed outline, so which is which
 * survives greyscale and does not depend on the legend.
 *
 * `d3-scale` and `d3-shape` supply the scales and the path generator; the marks
 * are rendered here, against the demo's own palette, like every other chart in
 * this folder.
 */

interface RidgelineChartProps {
    /** Optional: a summary produced before this field existed has none. */
    baseline: number[] | undefined
    tuned?: number[]
    labels: { baseline: string; tuned: string; axis: string; people: string }
    height?: number
}

const WIDTH = 320

export default function RidgelineChart({
    baseline,
    tuned,
    labels,
    height = 132,
}: RidgelineChartProps) {
    const clipId = useId()
    if (!baseline || baseline.length === 0) return null

    // Bin midpoints on a linear axis. The last bin is open-ended (Infinity), so
    // it is drawn one step wide rather than being dropped — a tail that vanishes
    // because it has no upper bound is the opposite of informative.
    const step = 1
    const xs = baseline.map((_, index) => index * step)
    const peak = Math.max(1, ...baseline, ...(tuned ?? []))

    const x = scaleLinear()
        .domain([0, baseline.length - 1])
        .range([0, WIDTH])
    const y = scaleLinear().domain([0, peak]).range([height, 0]).nice()

    const toArea = area<number>()
        .x((_, index) => x(xs[index]))
        .y0(height)
        .y1((value) => y(value))
        .curve(curveMonotoneX)

    const toLine = line<number>()
        .x((_, index) => x(xs[index]))
        .y((value) => y(value))
        .curve(curveMonotoneX)

    const series = tuned ?? baseline
    const axisTicks = [0, 3, 6, 8, 10].filter((index) => index < baseline.length)

    return (
        <figure className="m-0">
            <svg
                viewBox={`0 0 ${WIDTH} ${height + 26}`}
                className="w-full"
                role="img"
                aria-label={labels.axis}
                preserveAspectRatio="none"
            >
                <defs>
                    <clipPath id={clipId}>
                        <rect x={0} y={0} width={WIDTH} height={height} />
                    </clipPath>
                </defs>

                <line
                    x1={0}
                    y1={height}
                    x2={WIDTH}
                    y2={height}
                    stroke={STATUS_VAR.grid}
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                />

                <g clipPath={`url(#${clipId})`}>
                    <path
                        d={toArea(series) ?? undefined}
                        fill={SEQUENTIAL_VAR}
                        fillOpacity={0.18}
                        stroke="none"
                    />
                    <path
                        d={toLine(series) ?? undefined}
                        fill="none"
                        stroke={SEQUENTIAL_VAR}
                        strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                        strokeLinejoin="round"
                    />
                    {tuned && (
                        <path
                            d={toLine(baseline) ?? undefined}
                            fill="none"
                            stroke="currentColor"
                            strokeOpacity={0.45}
                            strokeWidth={1.5}
                            strokeDasharray="4 3"
                            vectorEffect="non-scaling-stroke"
                        />
                    )}
                </g>
            </svg>

            {/* Axis and legend in HTML, not SVG. `preserveAspectRatio="none"`
                above lets the plot fill any width, and that scales x and y
                independently — which would stretch every label into a smear.
                Text stays outside the box that gets distorted. */}
            <div className="relative mt-1 h-4">
                {axisTicks.map((index) => (
                    <span
                        key={index}
                        className="absolute -translate-x-1/2 text-[10px] tabular-nums text-muted-foreground"
                        style={{ left: `${(index / (baseline.length - 1)) * 100}%` }}
                    >
                        {Number.isFinite(HOURS_BIN_EDGES[index])
                            ? HOURS_BIN_EDGES[index]
                            : "∞"}
                    </span>
                ))}
            </div>

            <figcaption className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
                <span>{labels.axis}</span>
                {tuned && (
                    <>
                        <span className="flex items-center gap-1">
                            <span
                                aria-hidden
                                className="inline-block h-0 w-3 border-t-2 border-dashed"
                                style={{ borderColor: "currentColor", opacity: 0.5 }}
                            />
                            {labels.baseline}
                        </span>
                        <span className="flex items-center gap-1">
                            <span
                                aria-hidden
                                className="inline-block h-0.5 w-3 rounded"
                                style={{ background: SEQUENTIAL_VAR }}
                            />
                            {labels.tuned}
                        </span>
                    </>
                )}
            </figcaption>
        </figure>
    )
}
